import type { ReactNode, ButtonHTMLAttributes } from 'react';

export function Header({
  onHelp,
  onCascade,
  onCompact,
  onChat,
}: {
  onHelp?: () => void;
  onCascade?: () => void;
  onCompact?: () => void;
  onChat?: () => void;
}) {
  return (
    <header className="absolute left-0 right-0 top-0 z-10 flex h-[88px] items-center justify-end gap-2 px-l2">
      {onChat && (
        <button
          type="button"
          aria-label="Chat view"
          onClick={onChat}
          className="flex size-9 items-center justify-center rounded-[3px] text-content-muted transition-colors hover:bg-black/5 hover:text-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-shade-12"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path
              d="M3 4.5C3 3.67 3.67 3 4.5 3h9c.83 0 1.5.67 1.5 1.5v6c0 .83-.67 1.5-1.5 1.5H7l-3 3v-3h-.5C2.67 12 3 11.83 3 11V4.5z"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
      )}
      {onCompact && (
        <button
          type="button"
          aria-label="Compact view"
          onClick={onCompact}
          className="flex size-9 items-center justify-center rounded-[3px] text-content-muted transition-colors hover:bg-black/5 hover:text-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-shade-12"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="2" y="3" width="12" height="2" rx="1" fill="currentColor" />
            <rect x="2" y="7" width="12" height="2" rx="1" fill="currentColor" />
            <rect x="2" y="11" width="12" height="2" rx="1" fill="currentColor" />
          </svg>
        </button>
      )}
      {onCascade && (
        <button
          type="button"
          aria-label="View all steps"
          onClick={onCascade}
          className="flex size-9 items-center justify-center rounded-[3px] text-content-muted transition-colors hover:bg-black/5 hover:text-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-shade-12"
        >
          <svg width="20" height="14" viewBox="0 0 20 14" fill="none" aria-hidden="true">
            <rect x="0" y="0" width="20" height="2" rx="1" fill="currentColor" />
            <rect x="0" y="6" width="14" height="2" rx="1" fill="currentColor" />
            <rect x="0" y="12" width="8" height="2" rx="1" fill="currentColor" />
          </svg>
        </button>
      )}
      <TextButton onClick={onHelp}>Need help?</TextButton>
    </header>
  );
}

export function Button({
  children,
  disabled,
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'outline' }) {
  const base =
    'flex h-[40px] items-center justify-center gap-3 rounded-[6px] px-[17px] py-px text-[14px] font-medium leading-[20px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-shade-12';
  const variants =
    variant === 'outline'
      ? disabled
        ? 'cursor-not-allowed border border-[rgba(58,47,44,0.15)] text-[#887F7C]'
        : 'border border-[rgba(58,47,44,0.31)] text-content transition-colors hover:bg-black/5'
      : disabled
        ? 'cursor-not-allowed bg-[#E7E5E4] text-[#887F7C]'
        : 'bg-shade-12 text-shade-2 transition-opacity hover:opacity-90';
  return (
    <button
      type="button"
      disabled={disabled}
      className={`${base} ${variants} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function TextButton({
  children,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`flex h-[36px] items-center justify-center gap-s rounded-[6px] px-s text-[14px] font-medium leading-[20px] text-content transition-colors hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-shade-12 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function IconButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className="flex size-9 items-center justify-center rounded-[3px] text-content-muted transition-colors hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-shade-12"
      {...props}
    >
      {children}
    </button>
  );
}

export function VideoFrame({ children }: { children?: ReactNode }) {
  return (
    <div className="relative h-[540px] w-[960px] overflow-hidden rounded-[10px] bg-[#E2E2E2] shadow-[0_1px_1px_-0.1px_rgba(0,0,0,0.05),0_1.98px_3.06px_-0.36px_rgba(0,0,0,0.05),0_5.881px_14.045px_-1.944px_rgba(0,0,0,0.09),0_23.287px_85.958px_-13.997px_rgba(0,0,0,0.19)]">
      {children}
    </div>
  );
}

export function ProgressBar({ value }: { value: number }) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="h-[2px] w-full bg-[rgba(60,50,47,0.08)]">
      <div
        className="h-full bg-content transition-[width] duration-300 ease-out"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

export function BottomNav({
  onBack,
  onContinue,
  continueLabel = 'Continue',
  continueDisabled,
  showBack = true,
}: {
  onBack?: () => void;
  onContinue: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  showBack?: boolean;
}) {
  return (
    <div className="flex h-[80px] items-center justify-between bg-transparent px-l2">
      {showBack && onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="rounded-[6px] px-s py-1 text-[14px] font-normal leading-[20px] text-content-muted transition-colors hover:text-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-shade-12"
        >
          {'< Back'}
        </button>
      ) : (
        <span />
      )}
      <Button variant="outline" disabled={continueDisabled} onClick={onContinue}>
        {continueLabel}
      </Button>
    </div>
  );
}
